const FAKE_PHONE_VERIFICATION_CODES = Object.freeze({
  "+380333334567": "123456",
});

const PHONE_AUTH_DIAGNOSTICS_STORAGE_KEY = "phone-auth-diagnostics";
const PHONE_AUTH_DIAGNOSTICS_RETENTION_MS = 7 * 24 * 60 * 60 * 1000;
const PHONE_AUTH_DIAGNOSTICS_LIMIT = 20;

const PHONE_AUTH_ERROR_TRANSLATION_KEYS = Object.freeze({
  "auth/invalid-phone-number": "verification_invalid_phone",
  "auth/too-many-requests": "verification_too_many_requests",
  "auth/quota-exceeded": "verification_quota_exceeded",
  "auth/captcha-check-failed": "verification_captcha_failed",
  "auth/operation-not-allowed": "verification_not_configured",
  "auth/network-request-failed": "verification_network_failed",
  "auth/verification-timeout": "verification_timeout",
});

export function getFakePhoneVerificationCode(phoneNumber) {
  return FAKE_PHONE_VERIFICATION_CODES[phoneNumber] || null;
}

export function maskPhoneNumber(phoneNumber) {
  if (typeof phoneNumber !== "string" || !/^\+\d{8,15}$/.test(phoneNumber)) {
    return "[invalid phone]";
  }

  return `${phoneNumber.slice(0, 4)}${"*".repeat(
    phoneNumber.length - 6
  )}${phoneNumber.slice(-2)}`;
}

export function getPhoneAuthErrorTranslationKey(error) {
  return (
    PHONE_AUTH_ERROR_TRANSLATION_KEYS[error && error.code] ||
    "verification_request_failed"
  );
}

export function logFakeSms(phoneNumber, logger = console) {
  const verificationCode = getFakePhoneVerificationCode(phoneNumber);
  if (!verificationCode) {
    return;
  }

  logger.info(
    `[PhoneAuth][fake-sms] to=${phoneNumber} code=${verificationCode}`
  );
}

function getBrowserStorage() {
  try {
    return typeof window === "undefined" ? null : window.localStorage;
  } catch (error) {
    return null;
  }
}

function persistPhoneAuthError(details, storage) {
  if (!storage) {
    return;
  }

  try {
    const storedDiagnostics = JSON.parse(
      storage.getItem(PHONE_AUTH_DIAGNOSTICS_STORAGE_KEY) || "[]"
    );
    const diagnostics = Array.isArray(storedDiagnostics)
      ? storedDiagnostics
      : [];
    const retentionCutoff =
      Date.parse(details.recordedAt) - PHONE_AUTH_DIAGNOSTICS_RETENTION_MS;
    const retainedDiagnostics = diagnostics.filter(
      (diagnostic) => Date.parse(diagnostic.recordedAt) >= retentionCutoff
    );

    storage.setItem(
      PHONE_AUTH_DIAGNOSTICS_STORAGE_KEY,
      JSON.stringify(
        [...retainedDiagnostics, details].slice(-PHONE_AUTH_DIAGNOSTICS_LIMIT)
      )
    );
  } catch (error) {
    // Diagnostics must never block phone authentication error handling.
  }
}

export function logPhoneAuthError(
  phoneNumber,
  error,
  logger = console,
  stage = "unknown",
  storage = getBrowserStorage(),
  now = Date.now()
) {
  const details = {
    stage,
    code: (error && error.code) || "unknown",
    message: (error && error.message) || String(error),
    phoneNumber,
  };

  logger.error(`[PhoneAuth] request failed ${JSON.stringify(details)}`);
  persistPhoneAuthError(
    {
      recordedAt: new Date(now).toISOString(),
      ...details,
    },
    storage
  );
}

export function withPhoneAuthTimeout(request, timeoutMs = 60000) {
  let timeoutId;
  const timeout = new Promise((resolve, reject) => {
    timeoutId = setTimeout(() => {
      const error = new Error(
        "Firebase phone authentication did not respond in time."
      );
      error.code = "auth/verification-timeout";
      reject(error);
    }, timeoutMs);
  });

  return Promise.race([request, timeout]).finally(() => clearTimeout(timeoutId));
}
